import type { JsonValue } from "@protobuf-ts/runtime";
import { DecisionTool, CreateScenarioRequest } from "../../generated/backend/v1/plan";
import type { Date as PDate } from "../../generated/core/v1/date";
import { Lender, Loan, LoanType, Mortgage } from "../../generated/core/v1/debts";
import { Expense, HouseholdExpenseDetails_Type } from "../../generated/core/v1/expense";
import type { Family } from "../../generated/core/v1/family";
import { Job } from "../../generated/core/v1/person";
import { Property, PropertyKind } from "../../generated/core/v1/property";
import type { FieldEdit, Questionnaire } from "../../generated/core/v1/questionnaire";
import { PendingPayment, PendingPayment_Kind } from "../../generated/core/v1/tax";
import type { DateSchedule } from "../../generated/optimizer/v1/date_schedule";
import { Struct } from "../../generated/google/protobuf/struct";
import { addMonths, fromIso } from "../lib/dates";
import { monthlyPayment } from "../lib/amortization";
import { newUuid } from "../lib/uuid";
import { emptyQuestionnaire, fieldEdit } from "../questionnaire/build";

/**
 * Each Decisions tool turns its form into Questionnaire `changes` for POST /api/scenarios. The
 * engine applies them to a copy of the stored family (ApplyQuestionnaire); nothing is saved.
 *
 * New entities (an expense, a loan, a mortgage, a job) are added with list-item field edits
 * (`expenses[<new uuid>]` with the whole message as its proto3-JSON value), so the rest of the
 * family stays exactly as stored; the sections would replace whole lists and lose detail the
 * questionnaire can't express. Changes to existing entities are single-field edits.
 */

export type PeriodType = "weekly" | "monthly" | "yearly";

// ------------------------------------------------------------------ inputs (parsed form values)

export type ExpenseInput = {
  name: string;
  amount: number;
  dueDate: string; // ISO yyyy-mm-dd
  recurring: boolean;
  periodType: PeriodType;
  period: number;
  endDate?: string;
};

export type HomeInput = {
  name: string;
  price: number;
  downPayment: number;
  rate: number; // percent
  amortizationYears: number;
  purchaseDate: string;
  primaryResidence: boolean;
  annualPropertyTax?: number;
  annualInsurance?: number;
  lender: Lender;
};

export type JobAction = "keep" | "change" | "end";

export type JobInput = {
  existing: {
    personUuid: string;
    jobUuid: string;
    action: JobAction;
    income?: number;
    endDate?: string;
  }[];
  offers: {
    uuid: string;
    personUuid: string;
    title: string;
    employer: string;
    income: number;
    startDate: string;
    endDate?: string;
  }[];
};

export type CarInput = {
  name: string;
  price: number;
  downPayment: number;
  apr: number;
  termMonths: number;
  purchaseDate: string;
  insuranceMonthly?: number;
  maintenanceMonthly?: number;
  fuelMonthly?: number;
};

export type FinancedInput = {
  name: string;
  total: number;
  downPayment: number;
  apr: number;
  termMonths: number;
  purchaseDate: string;
  loanType: LoanType;
};

export type InsuranceEvent = "death" | "critical_illness";

export type InsuranceInput = {
  event: InsuranceEvent;
  personUuid: string;
  date: string;
  /** Critical illness: how long the income stops. */
  durationYears: number;
  /**
   * The coverage in force on the insured person for this event, paid out on the day: the amount
   * being tested. Undefined tests the coverage they hold now (`held`).
   */
  coverage?: number;
  /** What they hold now for this event's product (life, or critical illness), from step one. */
  held?: number;
  /**
   * What any coverage beyond `held` would cost, a month, from an insurer's quote: paid from today
   * until the event. Abi doesn't price insurance.
   */
  extraPremiumMonthly?: number;
};

// ------------------------------------------------------------------ helpers

function pd(iso: string | undefined): PDate | undefined {
  return iso ? fromIso(iso) : undefined;
}

function req(iso: string): PDate {
  const d = fromIso(iso);
  if (!d) throw new Error(`Not a date: ${iso}`);
  return d;
}

function schedule(type: PeriodType, period: number, anchor: PDate): DateSchedule {
  const p = Math.max(1, Math.floor(period || 1));
  if (type === "weekly") {
    return { schedule: { oneofKind: "nDayPeriodic", nDayPeriodic: { period: 7 * p } } };
  }
  if (type === "yearly") {
    return { schedule: { oneofKind: "yearlyRecurring", yearlyRecurring: { period: p } } };
  }
  return {
    schedule: {
      oneofKind: "monthlyRecurring",
      monthlyRecurring: { period: p, recurrenceDayOfMonth: anchor.day || 1 },
    },
  };
}

const monthly = (anchor: PDate) => schedule("monthly", 1, anchor);

function expense(opts: {
  name: string;
  amount: number;
  due: PDate;
  recurring?: { type: PeriodType; period: number };
  end?: PDate;
}): Expense {
  return Expense.create({
    uuid: newUuid(),
    name: opts.name,
    amountDuePerPeriod: opts.amount,
    lastKnownDueDate: opts.due,
    isRecurring: !!opts.recurring,
    dateSchedule: opts.recurring
      ? schedule(opts.recurring.type, opts.recurring.period, opts.due)
      : undefined,
    endDate: opts.end,
    details: {
      oneofKind: "householdExpenseDetails",
      householdExpenseDetails: { type: HouseholdExpenseDetails_Type.CUSTOM_EXPENSES },
    },
  });
}

const addExpense = (e: Expense): FieldEdit =>
  fieldEdit(`expenses[${e.uuid}]`, Expense.toJson(e) as JsonValue);

function loan(opts: {
  name: string;
  principal: number;
  apr: number;
  termMonths: number;
  type: LoanType;
  start: PDate;
}): Loan {
  const term = Math.max(1, Math.floor(opts.termMonths || 0));
  return Loan.create({
    uuid: newUuid(),
    name: opts.name,
    balance: opts.principal,
    interest: Math.max(0, opts.apr),
    type: opts.type,
    existingMonthlyPayment: monthlyPayment(opts.principal, opts.apr, term),
    lastKnownPaymentDueDate: opts.start,
    endPaymentDate: addMonths(opts.start, term),
    minPaymentSchedule: monthly(opts.start),
    accruementSchedule: monthly(opts.start),
  });
}

const addLoan = (l: Loan): FieldEdit => fieldEdit(`loans[${l.uuid}]`, Loan.toJson(l) as JsonValue);

function withEdits(today: PDate, edits: FieldEdit[]): Questionnaire {
  const q = emptyQuestionnaire(today);
  q.fieldEdits = edits;
  return q;
}

// ------------------------------------------------------------------ the tools

export function expenseChanges(input: ExpenseInput, today: PDate): Questionnaire {
  const due = req(input.dueDate);
  return withEdits(today, [
    addExpense(
      expense({
        name: input.name,
        amount: input.amount,
        due,
        recurring: input.recurring ? { type: input.periodType, period: input.period } : undefined,
        end: input.recurring ? pd(input.endDate) : undefined,
      }),
    ),
  ]);
}

export function homeChanges(input: HomeInput, today: PDate): Questionnaire {
  const when = req(input.purchaseDate);
  const propertyUuid = newUuid();
  const loanAmount = Math.max(0, input.price - input.downPayment);
  const edits: FieldEdit[] = [
    fieldEdit(
      `properties[${propertyUuid}]`,
      Property.toJson(
        Property.create({
          uuid: propertyUuid,
          name: input.name,
          kind: input.primaryResidence ? PropertyKind.PRINCIPAL_RESIDENCE : PropertyKind.OTHER,
          currentValue: input.price,
          purchasePrice: input.price,
          purchaseDate: when,
          annualPropertyTax: input.annualPropertyTax,
          annualInsurance: input.annualInsurance,
        }),
      ) as JsonValue,
    ),
  ];
  if (loanAmount > 0) {
    const m = Mortgage.create({
      uuid: newUuid(),
      name: input.name ? `${input.name} mortgage` : "Mortgage",
      balance: loanAmount,
      interest: input.rate,
      propertyUuid,
      purchaseDate: when,
      lender: input.lender,
      downPayment: input.downPayment,
      lastKnownPaymentDueDate: when,
      minPaymentSchedule: monthly(when),
      accruementSchedule: monthly(when),
      terms: {
        oneofKind: "remainingAmortizationMonths",
        remainingAmortizationMonths: Math.round(input.amortizationYears * 12),
      },
    });
    edits.push(fieldEdit(`mortgages[${m.uuid}]`, Mortgage.toJson(m) as JsonValue));
  }
  if (input.downPayment > 0) {
    edits.push(
      addExpense(
        expense({
          name: `${input.name || "Home"} down payment`,
          amount: input.downPayment,
          due: when,
        }),
      ),
    );
  }
  return withEdits(today, edits);
}

export function jobChanges(input: JobInput, family: Family, today: PDate): Questionnaire {
  const edits: FieldEdit[] = [];
  const known = new Set(family.persons.flatMap((p) => p.jobs.map((j) => `${p.uuid}/${j.uuid}`)));
  input.existing.forEach((j) => {
    if (!known.has(`${j.personUuid}/${j.jobUuid}`)) return;
    const base = `persons[${j.personUuid}].jobs[${j.jobUuid}]`;
    if (j.action === "change" && j.income !== undefined) {
      edits.push(fieldEdit(`${base}.pre_tax_annual_income`, j.income));
    }
    if (j.action === "end") {
      const end = pd(j.endDate) ?? today;
      edits.push(fieldEdit(`${base}.end_date`, end as unknown as JsonValue));
    }
  });
  input.offers.forEach((o) => {
    const start = req(o.startDate);
    const job = Job.create({
      uuid: o.uuid,
      title: o.title,
      employer: o.employer,
      preTaxAnnualIncome: o.income,
      startDate: start,
      endDate: pd(o.endDate),
    });
    edits.push(
      fieldEdit(`persons[${o.personUuid}].jobs[${job.uuid}]`, Job.toJson(job) as JsonValue),
    );
  });
  return withEdits(today, edits);
}

/** Fuel a month from consumption (L/100 km), distance (km a month) and price ($/L): a form preview. */
export function fuelMonthly(lPer100: number, kmMonthly: number, pricePerL: number): number {
  return (Math.max(0, lPer100) / 100) * Math.max(0, kmMonthly) * Math.max(0, pricePerL);
}

export function carChanges(input: CarInput, today: PDate): Questionnaire {
  const when = req(input.purchaseDate);
  const name = input.name || "Car";
  const edits: FieldEdit[] = [];
  const principal = Math.max(0, input.price - input.downPayment);
  if (input.downPayment > 0) {
    edits.push(
      addExpense(expense({ name: `${name} down payment`, amount: input.downPayment, due: when })),
    );
  }
  if (principal > 0) {
    edits.push(
      addLoan(
        loan({
          name: `${name} loan`,
          principal,
          apr: input.apr,
          termMonths: input.termMonths,
          type: LoanType.CAR_LOAN,
          start: when,
        }),
      ),
    );
  }
  const extras: [string, number | undefined][] = [
    ["insurance", input.insuranceMonthly],
    ["maintenance", input.maintenanceMonthly],
    ["fuel", input.fuelMonthly],
  ];
  extras.forEach(([what, amount]) => {
    if (!amount || amount <= 0) return;
    edits.push(
      addExpense(
        expense({
          name: `${name} ${what}`,
          amount,
          due: when,
          recurring: { type: "monthly", period: 1 },
        }),
      ),
    );
  });
  return withEdits(today, edits);
}

export function financedChanges(input: FinancedInput, today: PDate): Questionnaire {
  const when = req(input.purchaseDate);
  const name = input.name || "Purchase";
  const edits: FieldEdit[] = [];
  const principal = Math.max(0, input.total - input.downPayment);
  if (input.downPayment > 0) {
    edits.push(
      addExpense(expense({ name: `${name} down payment`, amount: input.downPayment, due: when })),
    );
  }
  if (principal > 0) {
    edits.push(
      addLoan(
        loan({
          name: `${name} loan`,
          principal,
          apr: input.apr,
          termMonths: input.termMonths,
          type: input.loanType,
          start: when,
        }),
      ),
    );
  }
  return withEdits(today, edits);
}

/** A person's salary from the family, before tax, a year (sum of their jobs). */
export function annualSalary(family: Family, personUuid: string): number {
  const p = family.persons.find((x) => x.uuid === personUuid);
  return (p?.jobs ?? []).reduce((s, j) => s + (j.preTaxAnnualIncome || 0), 0);
}

/** The coverage that pays out on the day: the amount tested, else what's held now. */
export function coverageInForce(input: InsuranceInput): number {
  return Math.max(0, input.coverage ?? input.held ?? 0);
}

/** Coverage beyond what's held now (input arithmetic: the tested amount less the held one). */
export function extraCoverage(input: InsuranceInput): number {
  return Math.max(0, coverageInForce(input) - Math.max(0, input.held ?? 0));
}

/**
 * The event as field edits: the insured person's jobs end on the day (and, for an illness, the same
 * work starts again after the time off), the coverage in force pays out on the day as a pending
 * payment, and the premium for any extra coverage is paid until then. No coverage formula: the
 * amount is the one the user chose to test.
 */
export function insuranceChanges(
  input: InsuranceInput,
  family: Family,
  today: PDate,
): Questionnaire {
  const when = req(input.date);
  const person = family.persons.find((p) => p.uuid === input.personUuid);
  if (!person) throw new Error("Pick who the analysis is for.");
  const edits: FieldEdit[] = [];
  const recovery =
    input.event === "critical_illness"
      ? addMonths(when, Math.round(Math.max(0, input.durationYears) * 12))
      : undefined;

  person.jobs.forEach((j) => {
    const base = `persons[${person.uuid}].jobs[${j.uuid}]`;
    edits.push(fieldEdit(`${base}.end_date`, when as unknown as JsonValue));
    if (recovery) {
      // The same work again once they've recovered.
      const back = Job.create({
        ...j,
        uuid: newUuid(),
        startDate: recovery,
        endDate: j.endDate,
        lastKnownPaymentDate: undefined,
      });
      edits.push(
        fieldEdit(`persons[${person.uuid}].jobs[${back.uuid}]`, Job.toJson(back) as JsonValue),
      );
    }
  });

  const payout = coverageInForce(input);
  if (payout > 0) {
    const pay = PendingPayment.create({
      uuid: newUuid(),
      kind: PendingPayment_Kind.OTHER,
      amount: payout,
      expectedDate: when,
      accountUuid: "",
    });
    edits.push(
      fieldEdit(
        `persons[${person.uuid}].pending_payments[${pay.uuid}]`,
        PendingPayment.toJson(pay) as JsonValue,
      ),
    );
  }

  const premium = input.extraPremiumMonthly ?? 0;
  if (premium > 0 && extraCoverage(input) > 0) {
    // The extra coverage's premium is paid until the claim.
    edits.push(
      addExpense(
        expense({
          name: "Extra insurance premiums",
          amount: premium,
          due: today,
          recurring: { type: "monthly", period: 1 },
          end: when,
        }),
      ),
    );
  }
  return withEdits(today, edits);
}

// ------------------------------------------------------------------ the request

/**
 * The scenario request: the changes (and, for insurance, the comparison's: run in the same job), plus the form values kept for display (`inputs`) and the
 * title it's saved under (`title.ts` `autoTitle`).
 */
export function scenarioRequest(
  tool: DecisionTool,
  changes: Questionnaire,
  inputs: Record<string, unknown>,
  title = "",
  comparisonChanges?: Questionnaire,
): CreateScenarioRequest {
  return CreateScenarioRequest.create({
    tool,
    changes,
    comparisonChanges,
    inputs: Struct.fromJson(JSON.parse(JSON.stringify(inputs)) as JsonValue),
    title,
  });
}
